import {
  Button,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DarkUpperSection,
  LoadingSpinner,
  PageSection,
  SearchField,
  SearchNotFoundCard,
  SearchPromptCard,
  StyledSelect,
  StyledSwitch,
  StyledTableHead,
  StyledTableRow
} from '../../components';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useObserveTripSheets from '../../hooks/useSubscribeTripsheet';
import { useCurrentUser, useDatastore } from '../../hooks';
import { galaxyNoteThreeDimensions } from '../../common/strings';
import {
  FormattedManifestLegStatus,
  getTripDisplayStatus,
  ManifestLegStatus,
  ManifestPackageStatus,
  ManifestStatus
} from '../../common/status';

const RECEIVE_MANIFEST_LIST_FILTERS_STORAGE_KEY = 'receiveManifestV2.listFilters';

type ReceiveManifestRouteState = {
  preserveReceiveManifestFilters?: boolean;
};

type ReceiveManifestListFilters = {
  search: string;
  searchStatus: string;
  hideCompleted: boolean;
  hideAllLocations: boolean;
};

const DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS: ReceiveManifestListFilters = {
  search: '',
  searchStatus: '',
  hideCompleted: true,
  hideAllLocations: true
};

function loadReceiveManifestListFilters(shouldPreserveFilters: boolean): ReceiveManifestListFilters {
  if (!shouldPreserveFilters) {
    sessionStorage.removeItem(RECEIVE_MANIFEST_LIST_FILTERS_STORAGE_KEY);
    return DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS;
  }

  try {
    const raw = sessionStorage.getItem(RECEIVE_MANIFEST_LIST_FILTERS_STORAGE_KEY);
    if (!raw) return DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS;

    const parsed = JSON.parse(raw) as Partial<ReceiveManifestListFilters>;
    return {
      search: typeof parsed.search === 'string' ? parsed.search : DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.search,
      searchStatus:
        typeof parsed.searchStatus === 'string'
          ? parsed.searchStatus
          : DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.searchStatus,
      hideCompleted:
        typeof parsed.hideCompleted === 'boolean'
          ? parsed.hideCompleted
          : DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.hideCompleted,
      hideAllLocations:
        typeof parsed.hideAllLocations === 'boolean'
          ? parsed.hideAllLocations
          : DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.hideAllLocations
    };
  } catch {
    return DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS;
  }
}

export default function ReceiveManifest() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as ReceiveManifestRouteState | null;
  const rawTripSheets = useObserveTripSheets();
  const user = useCurrentUser();
  const store = useDatastore();
  const shouldPreserveInitialFilters = Boolean(routeState?.preserveReceiveManifestFilters);
  const initialFilters = useMemo(
    () => loadReceiveManifestListFilters(shouldPreserveInitialFilters),
    [shouldPreserveInitialFilters]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState(initialFilters.search);
  const [searchStatus, setSearchStatus] = useState(initialFilters.searchStatus);
  const [hideCompleted, setHideCompleted] = useState(initialFilters.hideCompleted);
  const isMobile = useMediaQuery(galaxyNoteThreeDimensions);
  const [hideAllLocations, setHideAllLocations] = useState(initialFilters.hideAllLocations);
  const [usersLocation, setUsersLocation] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const tripSheets = useMemo(() => {
    return [...rawTripSheets].sort((a, b) =>
      String(b.reference ?? '').localeCompare(String(a.reference ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  }, [rawTripSheets]);

  useEffect(() => {
    async function getUserData() {
      const userDetails = await store.userDetails.get(user.cognitoUser.username, user.cognitoUser);
      const usersLocation = userDetails?.location;
      setUsersLocation(usersLocation?.name ?? '');
      setIsLoading(false);
    }
    getUserData();
  }, [store.userDetails, user.cognitoUser]);

  useEffect(() => {
    if (routeState?.preserveReceiveManifestFilters) {
      window.history.replaceState({ ...window.history.state, usr: {} }, '');
    }
  }, [routeState?.preserveReceiveManifestFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, searchStatus, hideCompleted, hideAllLocations]);

  useEffect(() => {
    sessionStorage.setItem(
      RECEIVE_MANIFEST_LIST_FILTERS_STORAGE_KEY,
      JSON.stringify({
        search,
        searchStatus,
        hideCompleted,
        hideAllLocations
      })
    );
  }, [hideAllLocations, hideCompleted, search, searchStatus]);

  const filteredTripSheets = useMemo(() => {
    let filtered = [...tripSheets];

    if (hideCompleted) {
      filtered = filtered
        .map((trip) => {
          const filteredLegs = trip.legs
            .map((leg) => {
              const filteredManifests = leg.manifests.filter(
                (manifest) => manifest.status !== ManifestStatus.completed
              );
              return { ...leg, manifests: filteredManifests };
            })
            .filter((leg) => leg.manifests.length > 0);
          return { ...trip, legs: filteredLegs };
        })
        .filter((trip) => trip.legs.length > 0);
    }

    if (searchStatus) {
      filtered = filtered
        .map((trip) => ({
          ...trip,
          legs: trip.legs.filter((leg) => leg.status.replace('_', '').toLowerCase() === searchStatus)
        }))
        .filter((trip) => trip.legs.length > 0);
    }

    if (search) {
      const lowerCaseSearch = search.toLowerCase();
      filtered = filtered
        .map((trip) => {
          const filteredLegs = trip.legs
            .map((leg) => {
              const filteredManifests = leg.manifests.filter(
                (manifest) =>
                  manifest.tripId.toLowerCase().includes(lowerCaseSearch) ||
                  manifest.id.toLowerCase().includes(lowerCaseSearch)
              );
              return { ...leg, manifests: filteredManifests };
            })
            .filter((leg) => leg.manifests.length > 0);

          return { ...trip, legs: filteredLegs };
        })
        .filter((trip) => trip.legs.length > 0);
    }

    if (hideAllLocations) {
      filtered = filtered.filter((trip) => {
        return trip.legs.some((leg) => leg.destination.businessName === usersLocation);
      });
    }
    return filtered;
  }, [hideAllLocations, hideCompleted, search, searchStatus, tripSheets, usersLocation]);

  const disabledRowStyle = {
    color: 'grey',
    height: '5em',
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  };

  const manifestStatuses = [
    { key: '', value: 'No Filter' },
    ...Object.entries(FormattedManifestLegStatus).map(([key, value]) => ({ key, value }))
  ];

  const hasActiveFilters =
    search !== DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.search ||
    searchStatus !== DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.searchStatus ||
    hideCompleted !== DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.hideCompleted ||
    hideAllLocations !== DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.hideAllLocations;

  function resetFilters() {
    setSearch(DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.search);
    setSearchStatus(DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.searchStatus);
    setHideCompleted(DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.hideCompleted);
    setHideAllLocations(DEFAULT_RECEIVE_MANIFEST_LIST_FILTERS.hideAllLocations);
    setCurrentPage(1);
    sessionStorage.removeItem(RECEIVE_MANIFEST_LIST_FILTERS_STORAGE_KEY);
  }

  function renderSelectAndSwitch() {
    return (
      <>
        <StyledSelect
          label={searchStatus === '' ? 'Filter Status' : ''}
          sx={{ width: '25%' }}
          shrink={false}
          value={searchStatus}
          onChange={setSearchStatus}>
          {manifestStatuses.map((status) => (
            <MenuItem key={status.value} value={status.key}>
              {status.value}
            </MenuItem>
          ))}
        </StyledSelect>
        <StyledSwitch value={hideCompleted} onChange={setHideCompleted} label="Hide Completed" />
        <StyledSwitch value={hideAllLocations} onChange={setHideAllLocations} label="Hide All Locations" />
        <Button
          variant="outlined"
          size="small"
          onClick={resetFilters}
          disabled={!hasActiveFilters}
          sx={{
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
            alignSelf: 'center',
            color: theme.palette.primary.contrastText,
            borderColor: 'rgba(255,255,255,0.78)',
            '&:hover': {
              borderColor: theme.palette.primary.contrastText,
              backgroundColor: 'rgba(255,255,255,0.12)'
            },
            '&.Mui-disabled': {
              color: 'rgba(255,255,255,0.42)',
              borderColor: 'rgba(255,255,255,0.24)'
            }
          }}>
          Reset Filters
        </Button>
      </>
    );
  }

  function handlePageChange(event: React.ChangeEvent<unknown>, value: number) {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const pageSize = 50;
  const totalNumberOfPages = Math.ceil(filteredTripSheets.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredTripSheets.length);
  const paginatedTripsheets = filteredTripSheets.slice(startIndex, endIndex);

  function shouldRouteManifestToV1(manifest: {
    status: ManifestStatus;
    ssccs: Array<{ packages: Array<{ receivedStatus: string }> }>;
  }) {
    if (manifest.status !== ManifestStatus.open) {
      return false;
    }

    return manifest.ssccs.some((sscc) =>
      sscc.packages.some(
        (pkg) =>
          pkg.receivedStatus === ManifestPackageStatus.received ||
          pkg.receivedStatus === ManifestPackageStatus.partial ||
          pkg.receivedStatus === ManifestPackageStatus.qm
      )
    );
  }

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={2} sx={{ padding: '1em 0' }}>
          <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
            Manifests
          </Typography>
          <Stack direction="row" spacing={2}>
            <SearchField
              placeholder="Search by Trip # or Manifest #"
              value={search}
              startIcon={<SearchIcon />}
              includeEndIcon
              onTrigger={(value) => setSearch(value)}
              sx={{ width: '26em' }}
            />

            {!isMobile && renderSelectAndSwitch()}
          </Stack>
          {isMobile && (
            <Stack direction="row" spacing="2em">
              {renderSelectAndSwitch()}
            </Stack>
          )}
        </Stack>
      </DarkUpperSection>
      <PageSection>
        <Stack spacing={2} sx={{ marginTop: '1em' }}>
          <LoadingSpinner isLoading={isLoading}>
            {search && paginatedTripsheets.length === 0 ? (
              <SearchNotFoundCard />
            ) : !search && paginatedTripsheets.length === 0 ? (
              <SearchPromptCard
                title="View Manifests"
                description="Search for manifests by Trip ID, Manifest ID, or use the filters."
                searchPrompt="Enter a Trip ID or Manifest ID above, or adjust your filters"
              />
            ) : (
              <Table>
                <StyledTableHead>
                  <TableCell width="12%">Trip #</TableCell>
                  <TableCell width="13%">Manifest #</TableCell>
                  <TableCell width="10%">Trailer #</TableCell>
                  <TableCell width="15%">Status</TableCell>
                  <TableCell width="12%">{isMobile ? '# Cons ' : '# Consignments'}</TableCell>
                  <TableCell width="19%">From</TableCell>
                  <TableCell width="19%">To</TableCell>
                </StyledTableHead>
                <TableBody>
                  {paginatedTripsheets.map((trip) => {
                    return trip.legs.map((leg) => {
                      return leg.manifests
                        .filter((m) => m.ssccs.length > 0)
                        .map((manifest) => {
                          const readOnly =
                            manifest.ssccs.length === 0 || leg.status === ManifestLegStatus.awaitingDeparture;

                          return (
                            <StyledTableRow
                              key={manifest.id}
                              sx={readOnly ? disabledRowStyle : { height: '5em', cursor: 'pointer' }}
                              onClick={() => {
                                if (!readOnly) {
                                  const tripId = trip.id;
                                  const legStatus = leg.status;
                                  const legDestination = leg.destination.businessName;
                                  const tripReference = trip.reference || '';

                                  const searchParams = new URLSearchParams({
                                    tripId,
                                    legStatus,
                                    legDestination,
                                    manifestUiVersion: shouldRouteManifestToV1(manifest) ? 'v1' : 'v2',
                                    tripReference
                                  });

                                  navigate(`/receive-manifest/${manifest.id}?${searchParams.toString()}`, {
                                    state: {
                                      tripId,
                                      legStatus,
                                      legDestination,
                                      tripReference
                                    }
                                  });
                                }
                              }}>
                              <TableCell>{manifest.tripId}</TableCell>
                              <TableCell>{manifest.id}</TableCell>
                              <TableCell>{manifest.trailerId}</TableCell>
                              <TableCell>
                                {getTripDisplayStatus({
                                  manifestStatus: manifest.status,
                                  legStatus: leg.status
                                })}
                              </TableCell>
                              <TableCell>{manifest.ssccs.length}</TableCell>
                              <TableCell>{leg.origin.businessName}</TableCell>
                              <TableCell>{leg.destination.businessName}</TableCell>
                            </StyledTableRow>
                          );
                        });
                    });
                  })}
                </TableBody>
              </Table>
            )}
          </LoadingSpinner>
        </Stack>
        <Stack spacing={2} direction="row" justifyContent="center" sx={{ paddingTop: '1em', paddingBottom: '1em' }}>
          <Pagination count={totalNumberOfPages} page={currentPage} onChange={handlePageChange} color="primary" />
        </Stack>
      </PageSection>
    </>
  );
}
